import * as Dialog from '@radix-ui/react-dialog';
import { ListFilter, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import LoadingState from '../../components/common/LoadingState.jsx';
import ProductGrid from '../../components/storefront/product/ProductGrid.jsx';
import Seo from '../../components/common/Seo.jsx';
import { useAppData } from '../../contexts/AppDataContext.jsx';
import { storefrontService } from '../../services/storefrontService.js';

const sortOptions = [
  ['best-selling', 'Best selling'],
  ['latest', 'Latest'],
  ['a-z', 'Alphabetically, A-Z'],
  ['z-a', 'Alphabetically, Z-A'],
  ['price-low-high', 'Price, low to high'],
  ['price-high-low', 'Price, high to low'],
];

function FilterContent({ categoryLinks, saleOnly, onSaleToggle, onNavigate }) {
  return (
    <>
      <div className="shop-filter-group">
        <h3 className="shop-filter-title">Categories</h3>
        <div className="shop-filter-options">
          {categoryLinks.map((item) => (
            <Link className={`shop-check-row ${item.active ? 'active' : ''}`} key={item.id} to={item.href} onClick={onNavigate}>
              <span className="shop-check-box" />
              <span>{item.name}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="shop-filter-group">
        <h3 className="shop-filter-title">Filters</h3>
        <button
          className={`shop-check-row shop-check-button ${saleOnly ? 'active' : ''}`}
          type="button"
          aria-pressed={Boolean(saleOnly)}
          onClick={onSaleToggle}
        >
          <span className="shop-check-box" />
          <span>On sale only</span>
        </button>
      </div>
    </>
  );
}

export default function CategoryPage() {
  const { type, id } = useParams();
  const { data } = useAppData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sort = searchParams.get('sort') || 'best-selling';
  const saleOnly = searchParams.get('sale_only') || '';

  const params = useMemo(() => ({ type, id, sort, sale_only: saleOnly }), [type, id, sort, saleOnly]);
  const productRoot = useMemo(() => {
    const navigation = data?.navigation || [];
    return navigation.find((item) => item.name?.toLowerCase() === 'products') || navigation[0];
  }, [data]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    storefrontService.products(params)
      .then((data) => {
        if (!active) return;
        setPayload(data);
        setStatus('ready');
      })
      .catch((err) => {
        if (!active) return;
        setError(err);
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [params]);

  function updateParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  if (status === 'loading') return <LoadingState label="Loading products" />;
  if (status === 'error') {
    return <div className="container page-block"><h1>Products unavailable</h1><p>{error.message}</p></div>;
  }

  const productCount = payload.products.length;
  const pageTitle = payload.category.title === 'Products' ? 'All Products' : payload.category.title;
  const allProductsActive = type === 'top-category' && String(id) === String(productRoot?.id);
  const categoryLinks = [
    productRoot ? {
      id: `top-${productRoot.id}`,
      name: 'All products',
      href: productRoot.href,
      active: allProductsActive,
    } : null,
    ...(productRoot?.children || []).map((item) => ({
      id: `mid-${item.id}`,
      name: item.name,
      href: item.href,
      active: type === 'mid-category' && String(id) === String(item.id),
    })),
  ].filter(Boolean);

  return (
    <section className="container page-section shop-page">
      <Seo
        title={`Shop ${pageTitle}`}
        description={`Shop ${pageTitle.toLowerCase()} from La Bella Trading — raw Mediterranean honey, bee products, olive oil, and natural soaps shipped across Canada.`}
        path={`/category/${type}/${id}`}
      />
      <aside className="shop-sidebar" aria-label="Product filters">
        <FilterContent
          categoryLinks={categoryLinks}
          saleOnly={saleOnly}
          onSaleToggle={() => updateParam('sale_only', saleOnly ? '' : '1')}
        />
      </aside>

      <div className="shop-results">
        <div className="shop-results-head">
          <div>
            <span className="eyebrow">Shop</span>
            <h1>{pageTitle} <span>({productCount})</span></h1>
          </div>
          <div className="shop-actions-row">
            <Dialog.Root open={filtersOpen} onOpenChange={setFiltersOpen}>
              <Dialog.Trigger asChild>
                <button className="shop-filter-toggle" type="button">
                  <ListFilter size={15} /> Filter
                </button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="shop-filter-overlay" />
                <Dialog.Content className="shop-filter-drawer" aria-describedby={undefined}>
                  <div className="shop-drawer-head">
                    <Dialog.Title>Filters</Dialog.Title>
                    <Dialog.Close className="shop-drawer-close" type="button" aria-label="Close filters">
                      <X size={18} />
                    </Dialog.Close>
                  </div>
                  <div className="shop-drawer-body">
                    <FilterContent
                      categoryLinks={categoryLinks}
                      saleOnly={saleOnly}
                      onSaleToggle={() => updateParam('sale_only', saleOnly ? '' : '1')}
                      onNavigate={() => setFiltersOpen(false)}
                    />
                  </div>
                  <div className="shop-drawer-foot">
                    <Dialog.Close className="shop-drawer-apply" type="button">
                      Show {productCount} {productCount === 1 ? 'product' : 'products'}
                    </Dialog.Close>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
            <label className="shop-sort-control">
              <ListFilter size={17} />
              <select value={sort} onChange={(event) => updateParam('sort', event.target.value)} aria-label="Sort products">
                {sortOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>
        </div>
        <ProductGrid className="shop-product-grid" products={payload.products} />
      </div>
    </section>
  );
}
